import { useQuery } from '@apollo/react-hooks';
import { GET_ADMISSION_STEP_DATA } from './GET_ADMISSION_STEP_DATA.gql';
import type {
  AdmissionForm,
  Condition,
  Conditions,
  QualificationCheckValue,
} from '../types';
import { PROGRAMME, QUALIFICATION_CHECK_ITEM } from '../constants';
import type { AdmissionStepData } from './types';
import { OFFER_TYPE, DSRC_DECISION_TYPE } from '../../constants';
import {
  type PreliminaryCheckRule,
  Programme,
  PreliminaryCheckItem,
  Programme_Choice_Study_Mode,
  Funding_Source,
  Dsrc_Decision_Type,
  OfferSuggestion,
  EntranceRequirement,
} from '../../../../../../graphql/graphql';
import type { SuggestOffer } from '../../../../../Nomination/graphql';
import {
  getProgrammeType,
  type ProgrammeTypes,
} from '../../../../../Nomination';
import { PAGE_TEXT } from '../../../../lang';

const ADMISSION_FORM_TEXT = PAGE_TEXT.DRC_ASSESSMENT_DETAILS.ASSESSMENT.STEP_G;

const converResultStr = (str: string): string => {
  return (
    str?.charAt(0)?.toUpperCase() +
    str?.slice(1).toLowerCase().replace('_', ' ')
  );
};

const convertQualificationCheckData = (
  qualificationCheckData: PreliminaryCheckRule[] | undefined,
  targetProgrammeLevel: any,
  targetDuration: number,
  itemKeyMap: Record<QUALIFICATION_CHECK_ITEM, string>,
): QualificationCheckValue => {
  const programmeQualificationData = qualificationCheckData?.find(
    ({ programmeLevel, duration }: any) => {
      return (
        programmeLevel === targetProgrammeLevel && duration === targetDuration
      );
    },
  );

  const items: Partial<Record<QUALIFICATION_CHECK_ITEM, string>> =
    Object.entries(itemKeyMap).reduce((acc, [targetItem, originalItem]) => {
      const found = programmeQualificationData?.checkRule?.find(({ item }) => {
        return item === originalItem;
      })?.result;
      return { ...acc, [targetItem]: converResultStr(found as string) };
    }, {});

  return {
    result: converResultStr(programmeQualificationData?.result as string),
    items: items as Record<QUALIFICATION_CHECK_ITEM, string>,
  };
};

const convertConditionData = (
  conditions: any[] | undefined | null,
  conditionDetails: any,
  targetCondition: EntranceRequirement,
): Condition => {
  const ticked = conditions?.includes(targetCondition) ?? false;
  const detail = conditionDetails?.find((conditionDetail: any) => {
    return conditionDetail.requirement === targetCondition;
  })?.detail;
  const detailType = (detail as boolean) ? 'custom' : 'default';
  return {
    ticked,
    detailType,
    detail: detail ?? '',
  };
};

export const useGetAdmissionStepData = (
  applicationId: string,
  entryYear: string,
  nominationFilter: any,
  assessmentFilter: any,
): AdmissionStepData => {
  const { data, loading, error, refetch } = useQuery(GET_ADMISSION_STEP_DATA, {
    variables: { applicationId, entryYear, nominationFilter, assessmentFilter },
    fetchPolicy: 'network-only',
  });

  // get qualification checks data
  const qualificationCheckData = data?.preliminaryCheck?.rules;
  const mphil2QualificationData = convertQualificationCheckData(
    qualificationCheckData,
    Programme.MasterOfPhilosophy,
    2,
    {
      [QUALIFICATION_CHECK_ITEM.GENERAL]: PreliminaryCheckItem.RequirementMphil,
      [QUALIFICATION_CHECK_ITEM.ENGLISH]:
        PreliminaryCheckItem.EnglishRequirement,
    },
  );
  const phd3QualificationData = convertQualificationCheckData(
    qualificationCheckData,
    Programme.DoctorOfPhilosophy,
    3,
    {
      [QUALIFICATION_CHECK_ITEM.GENERAL]:
        PreliminaryCheckItem.RequirementPhdThreeOrSix,
      [QUALIFICATION_CHECK_ITEM.ENGLISH]:
        PreliminaryCheckItem.EnglishRequirement,
    },
  );
  const phd42QualificationData = convertQualificationCheckData(
    qualificationCheckData,
    Programme.DoctorOfPhilosophy,
    4,
    {
      [QUALIFICATION_CHECK_ITEM.GENERAL]:
        PreliminaryCheckItem.RequirementPhdFourOrEight,
      [QUALIFICATION_CHECK_ITEM.ENGLISH]:
        PreliminaryCheckItem.EnglishRequirement,
    },
  );
  const qualificationChecks = {
    [PROGRAMME.MPHIL_2]: mphil2QualificationData,
    [PROGRAMME.PHD_3]: phd3QualificationData,
    [PROGRAMME.PHD_4]: phd42QualificationData,
  };

  const suggestOffers = qualificationCheckData
    ?.filter(
      rule =>
        rule.result !== OfferSuggestion.Reject &&
        rule.result !== OfferSuggestion.NotRecommended,
    )
    ?.flatMap(rule =>
      getProgrammeType(rule.programmeLevel, rule.duration as number).map(
        programme => ({
          programme,
          offerType: rule.result?.includes(OfferSuggestion.Firm)
            ? OFFER_TYPE.FIRM
            : OFFER_TYPE.CONDITION,
        }),
      ),
    ) as SuggestOffer[];

  const nomination = data?.nominations?.[0];
  // programme

  const programme = nomination?.admission?.programme ?? '';
  const nominatedProgramme =
    ADMISSION_FORM_TEXT.ADMISSION.NOMINATED_ADMISSION?.[
      `${programme as ProgrammeTypes}`
    ] ?? 'N/A';

  // requestRequirementWaive
  const englishFulfilled = Boolean(
    nomination?.admission?.requirements?.englishLanguageRequirements?.[0]
      ?.fulfilled,
  );
  const generalFulfilled = Boolean(
    nomination?.admission?.requirements?.generalEntranceRequirements?.[0]
      ?.fulfilled,
  );
  const requestRequirementWaive = !englishFulfilled || !generalFulfilled; // false means entranceRequirementWaive is required

  // programmeRequirements
  const programmeRequirements = {
    [EntranceRequirement.ConditionalEnglish]:
      nomination?.admission?.requirements?.englishLanguageRequirements?.[0]
        ?.requirements,
    [EntranceRequirement.ConditionalGeneral]:
      nomination?.admission?.requirements?.generalEntranceRequirements?.[0]
        ?.requirements,
    [EntranceRequirement.ConditionalOthers]: 'GMAT/GRE',
    [EntranceRequirement.ConditionalAdditional]: 'Additional Requirements',
  };

  // maxMeetConditionDeadline
  const maxMeetConditionDeadline =
    data?.entryYearInformation?.registrationDeadline;

  // needStipend
  const needStipend =
    data?.application.programmeChoice?.studyMode ===
    Programme_Choice_Study_Mode.FullTime;

  // isSelfFinance
  const isSelfFinance = Boolean(
    nomination?.fundings?.find(funding => {
      return funding.source === Funding_Source.SelfFinanced;
    }),
  );

  const drcAdmissionData = data?.dsrcAdmission;
  const {
    dsrcDecision,
    rejectionRemarks,
    remarks,
    entranceRequirementWaive,
    isFirmOffer,
    conditions: conditionRequirements,
    conditionDetails,
    contactDepartmentPersonName,
    contactDepartmentPersonPhone,
    contactDepartmentPersonEmail,
    conditionFulfillingDeadline,
    tuitionAssistantshipType,
    tuitionFeeAccountCode,
    stipendApproval,
    plagiarismCheck,
    plagiarismFound,
    confirmationChairInTeam,
    confirmationDoRPgSInTeam,
  } = drcAdmissionData ?? {};

  const hasCreated = dsrcDecision !== undefined && dsrcDecision !== null;

  let newDsrcDecision = dsrcDecision as DSRC_DECISION_TYPE | undefined;
  let returnDestination: DSRC_DECISION_TYPE | undefined;

  if (
    dsrcDecision != null &&
    [
      Dsrc_Decision_Type.ReturnToApplicant,
      Dsrc_Decision_Type.ReturnToSupervisor,
    ].includes(dsrcDecision)
  ) {
    returnDestination = newDsrcDecision;
    newDsrcDecision = DSRC_DECISION_TYPE.RETURN;
  }

  let offerType;
  if (typeof isFirmOffer === 'boolean') {
    offerType = isFirmOffer ? OFFER_TYPE.FIRM : OFFER_TYPE.CONDITION;
  }

  const newConditions: Partial<Conditions> = Object.values(
    EntranceRequirement,
  ).reduce((acc, value) => {
    return {
      ...acc,
      [value]: convertConditionData(
        conditionRequirements,
        conditionDetails,
        value,
      ),
    };
  }, {});

  const defaultStipendApproval = isSelfFinance ? false : undefined;
  const newStipendApproval = stipendApproval ?? defaultStipendApproval;
  const convertedStipendApproval =
    typeof newStipendApproval === 'boolean'
      ? String(newStipendApproval)
      : undefined;

  const supervisorArr = data?.nominations?.[0]?.supervisors;
  const hasDrcChair = supervisorArr?.some(sup => sup?.isDrcChair);
  const hasDoRPgS = supervisorArr?.some(sup => sup?.isDoRPgS);

  const admissionForm: Partial<AdmissionForm> = {
    admissionDecision: newDsrcDecision ?? '',
    returnDestination: returnDestination ?? '',
    remarks: remarks ?? '',
    entranceRequirementWaive: requestRequirementWaive
      ? (entranceRequirementWaive ?? undefined)
      : undefined,
    offerType: offerType ?? '',
    conditions: newConditions as Conditions,
    contactDepartmentPersonName: contactDepartmentPersonName ?? '',
    contactDepartmentPersonPhone: contactDepartmentPersonPhone ?? '',
    contactDepartmentPersonEmail: contactDepartmentPersonEmail ?? '',
    conditionFulfillingDeadline: conditionFulfillingDeadline ?? '',
    tuitionAssistantshipType: tuitionAssistantshipType ?? '',
    rejectionRemarks: rejectionRemarks ?? '',
    tuitionFeeAccountCode: tuitionFeeAccountCode ?? '',
    stipendApproval: convertedStipendApproval,
    plagiarismCheck: plagiarismCheck ?? false,
    plagiarismNotFound: !(plagiarismFound ?? false),
    confirmationChairInTeam: confirmationChairInTeam ?? hasDrcChair,
    confirmationDoRPgSInTeam: confirmationDoRPgSInTeam ?? hasDoRPgS,
  };

  return {
    qualificationChecks,
    suggestOffers,
    nominatedProgramme,
    requestRequirementWaive,
    programmeRequirements,
    maxMeetConditionDeadline,
    needStipend,
    isSelfFinance,
    hasCreated,
    admissionForm,
    loading,
    error,
    refetch,
  };
};
